/* DryStep (ydm) carousels: the collection reviews and the homepage "shop by" slider. Swipe,
   trackpad and keyboard scrolling are native (scroll-snap). This adds the arrows (one slide per
   press, each arrow greys out at its end, both hide when everything fits) and dragging with a
   mouse, which settles on the nearest slide and does not count as a click on a link.

   A carousel with data-autoplay="<ms>" also loops and moves on by itself (the homepage reviews, Dan,
   Slack 17 October, "revolving horizontally (like Hears)"). The slides are cloned once after the
   originals; when the row comes to rest on a clone it jumps back by one set, which looks the same, so
   it runs round for ever and the arrows never grey out. It moves one slide every <ms>: on while the
   pointer is over it (Jack's call on the feature player, 16 October), waiting <ms> again after any
   tap, swipe, drag or arrow, holding while a finger or the mouse is down or the keyboard is inside it,
   only while half of it is on screen, and never for reduced motion. Carousels without the attribute
   are unchanged. */

// In a block so the class is scoped to this load: the file is included by several sections on one page,
// and a second top-level class declaration throws "YdmCarousel has already been declared".
{
class YdmCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.update());
      },
      { passive: true }
    );
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.track);
    if (this.hasAttribute('data-autoplay')) this.loop();
    this.update();
    this.drag();
  }

  loop() {
    const originals = [...this.track.children];
    // Nothing to loop when every slide already fits
    if (originals.length < 2 || this.track.scrollWidth - this.track.clientWidth <= 2) return;
    this.looping = true;
    this.count = originals.length;
    for (const slide of originals) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      this.track.appendChild(clone);
    }

    // At rest on a clone: jump back one set to the original in the same place
    let settle = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(settle);
        settle = setTimeout(() => this.wrap(), 150);
      },
      { passive: true }
    );

    const delay = parseInt(this.dataset.autoplay, 10) || 5000;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let last = performance.now();
    let held = false;
    let inView = false;
    const touched = () => {
      last = performance.now();
    };
    this.addEventListener('pointerdown', () => {
      held = true;
      touched();
    });
    const release = () => {
      held = false;
      touched();
    };
    this.addEventListener('pointerup', release);
    this.addEventListener('pointercancel', release);
    this.addEventListener('lostpointercapture', release);
    this.addEventListener('touchstart', touched, { passive: true });
    this.addEventListener('touchend', touched, { passive: true });
    this.addEventListener('wheel', touched, { passive: true });
    this.addEventListener('keydown', touched);
    new IntersectionObserver((entries) => {
      // The first review gets its full time once the row comes into view, however long it took to get there
      if (entries[0].isIntersecting && !inView) touched();
      inView = entries[0].isIntersecting;
    }, { threshold: 0.5 }).observe(this);
    this.timer = setInterval(() => {
      if (!inView || held || document.hidden) return;
      if (this.track.classList.contains('is-dragging') || this.querySelector(':focus-visible')) return;
      if (performance.now() - last < delay) return;
      last = performance.now();
      this.step(1);
    }, 200);
  }

  // Scroll distance of one full set of slides: where the first clone sits
  setWidth() {
    return this.starts()[this.count];
  }

  wrap() {
    if (!this.looping || this.track.classList.contains('is-dragging')) return;
    const set = this.setWidth();
    const x = this.track.scrollLeft;
    if (set > 0 && x >= set - 2) {
      this.track.scrollTo({ left: x - set, behavior: 'instant' });
    }
  }

  drag() {
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    let down = false;
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      down = true;
      moved = false;
      startX = event.clientX;
      startLeft = this.track.scrollLeft;
    });
    this.track.addEventListener('pointermove', (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < 5) return;
      if (!moved) {
        moved = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
      }
      this.track.scrollLeft = startLeft - dx;
    });
    const end = () => {
      if (!down) return;
      down = false;
      if (!moved) return;
      this.track.classList.remove('is-dragging');
      const x = this.track.scrollLeft;
      const nearest = this.starts().reduce((a, b) => (Math.abs(b - x) < Math.abs(a - x) ? b : a), 0);
      this.track.scrollTo({ left: nearest, behavior: 'smooth' });
    };
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);
    // A drag is not a click on whatever link it started over
    this.track.addEventListener('click', (event) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearInterval(this.timer);
  }

  // The scroll position at which each slide sits where the first one rests. Every slide snaps to that
  // spot, so measuring from the first slide needs neither offsetLeft (which counts from the nearest
  // positioned ancestor) nor the track's scroll-padding (which read as 0 on the collection carousel while
  // its padding put the first slide 40px in), and every press moves one slide.
  starts() {
    const slides = [...this.track.children];
    if (!slides.length) return [];
    const first = slides[0].getBoundingClientRect().left;
    return slides.map((slide) => slide.getBoundingClientRect().left - first);
  }

  step(direction) {
    let x = this.track.scrollLeft;
    // Looping and at the very start: stand on the same place one set along, so there is a slide behind
    if (this.looping && direction < 0 && x <= 2) {
      x = this.setWidth();
      this.track.scrollTo({ left: x, behavior: 'instant' });
    }
    const starts = this.starts();
    let target;
    if (direction > 0) {
      target = starts.find((left) => left > x + 4);
      if (target === undefined) target = this.track.scrollWidth;
    } else {
      target = [...starts].reverse().find((left) => left < x - 4);
      if (target === undefined) target = 0;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    this.toggleAttribute('data-static', max <= 2);
    if (this.prev) this.prev.disabled = !this.looping && x <= 2;
    if (this.next) this.next.disabled = !this.looping && x >= max - 2;
  }
}

if (!customElements.get('ydm-carousel')) customElements.define('ydm-carousel', YdmCarousel);
}
